/**
 * Report 3, product item 11: the buy column travels with the photos.
 *
 * The column is taller than the window, so a plain `top` sticky never moves and
 * `bottom` sticky is ignored by the browser for this grid item (measured: the
 * box keeps scrolling past the pin line). The reference solves it with a
 * negative top offset - its column is 1538 tall in a 900 window and sits at
 * top: -658px, which parks the bottom edge 20px above the window bottom and
 * releases the column when the gallery ends.
 *
 * The offset is recomputed on load and on resize only, never on an accordion
 * toggle: the reference keeps its offset unchanged there, so an opening panel
 * grows downward and the clicked row stays exactly where it was.
 */
const GAP = 20;
const TWO_COLUMN_WIDTH = 1000;
const SELECTOR = '.product-information .product-details';

let pending = false;

/** The column is pinned once it has been pushed up to its own offset. */
function isPinned(column) {
  const offset = parseFloat(column.style.getPropertyValue('--details-sticky-top'));
  if (Number.isNaN(offset)) return false;
  return column.getBoundingClientRect().top <= offset + 1;
}

function update() {
  const column = document.querySelector(SELECTOR);
  if (!column) return;

  if (window.innerWidth < TWO_COLUMN_WIDTH) {
    column.style.removeProperty('--details-sticky-top');
    return;
  }

  // Never re-anchor a column the reader is already looking at: an opening
  // accordion would yank it. The new offset waits until the column is free.
  if (isPinned(column)) {
    pending = true;
    return;
  }
  pending = false;

  const headerOffset =
    parseFloat(getComputedStyle(column).getPropertyValue('--sticky-header-offset')) || 0;
  const height = column.getBoundingClientRect().height;
  const top = Math.min(headerOffset, window.innerHeight - height - GAP);

  column.style.setProperty('--details-sticky-top', `${Math.round(top)}px`);
}

let frame = null;
function schedule() {
  if (frame) cancelAnimationFrame(frame);
  frame = requestAnimationFrame(() => {
    frame = null;
    update();
  });
}

function observe() {
  const column = document.querySelector(SELECTOR);
  if (!column || typeof ResizeObserver === 'undefined') return;
  // The height is wrong until the layout settles - 3237 against 1479 measured on
  // organic-cotton-shorts-truffle at DOMContentLoaded - so the offset follows the
  // column instead of being taken once.
  new ResizeObserver(schedule).observe(column);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    schedule();
    observe();
  }, { once: true });
} else {
  schedule();
  observe();
}
window.addEventListener('load', schedule, { once: true });
window.addEventListener('resize', schedule);
window.addEventListener('scroll', () => {
  if (pending) schedule();
}, { passive: true });
document.addEventListener('shopify:section:load', schedule);
